/*
  Reihenfolge-Einstellungen für Mitarbeitende und Abteilungen.

  Sortiert wird per Ziehen am Griff oder mit den Pfeilen. Beim Ziehen zeigt
  eine Linie, zwischen welche zwei Zeilen der Eintrag fällt: oberhalb der
  Zeile, über deren obere Hälfte der Zeiger steht, sonst darunter. Gezogen
  wird die ganze Zeile, nicht nur der Griff.

  Gespeichert wird im Hintergrund – die Liste bleibt bedienbar, während der
  vorige Schritt noch unterwegs ist. Früher war sie bis zum Neuladen des
  ganzen Plans gesperrt, nach jedem einzelnen Schritt. Folgen mehrere Schritte
  schnell aufeinander, geht nur der neueste Stand zum Server, sobald der
  laufende durch ist. Schlägt das Speichern fehl, sagt es ein Toast, und der
  Plan holt den Stand des Servers zurück.
*/

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
// Dasselbe Symbol wie in /settings fuer "Abteilungen & Schichten".
import IconDepartment from "@mui/icons-material/CorporateFareOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { IconDrag } from "../../../../design-system/icons";
import ShiftplanBusyDots from "../ui/ShiftplanBusyDots";
import ShiftplanTooltip from "../tooltips/ShiftplanTooltip";

const ORDER_MODES = Object.freeze({
  EMPLOYEE: "employee",
  DEPARTMENT: "department",
});

const SAVE_STATUS = Object.freeze({
  IDLE: "idle",
  SAVING: "saving",
  SAVED: "saved",
});

// So lange steht "Gespeichert" nach dem letzten Schritt.
const SAVED_HINT_DURATION = 2400;
const ERROR_TOAST_DURATION = 4200;
const EMPTY_DRAG = Object.freeze({ fromIndex: null, insertIndex: null });

function normalizeRows(rows, idField) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    id: String(row?.[idField] ?? row?.id ?? ""),
    name: row?.name || "Unbenannt",
    metaLabel: row?.email || row?.metaLabel || "",
  })).filter((row) => row.id && row.id !== "undefined");
}

function moveItem(rows, index, direction) {
  const targetIndex = direction === "up" ? index - 1 : index + 1;
  if (index < 0 || targetIndex < 0 || targetIndex >= rows.length) return rows;

  const nextRows = [...rows];
  [nextRows[index], nextRows[targetIndex]] = [nextRows[targetIndex], nextRows[index]];
  return nextRows;
}

function moveItemToIndex(rows, fromIndex, toIndex) {
  const sourceIndex = Number(fromIndex);
  const targetIndex = Number(toIndex);
  const isValidMove =
    Number.isInteger(sourceIndex) &&
    Number.isInteger(targetIndex) &&
    sourceIndex >= 0 &&
    targetIndex >= 0 &&
    sourceIndex < rows.length &&
    targetIndex < rows.length &&
    sourceIndex !== targetIndex;

  if (!isValidMove) return rows;

  const nextRows = [...rows];
  const [movedRow] = nextRows.splice(sourceIndex, 1);
  nextRows.splice(targetIndex, 0, movedRow);
  return nextRows;
}

/*
  Verschiebt an eine Einfügestelle zwischen den Zeilen: 0 heisst vor der
  ersten, rows.length nach der letzten. Liegt die Stelle unterhalb des
  Eintrags, rückt sie um eins nach oben, weil er selbst aus der Liste fällt.
*/
export function moveItemToInsertIndex(rows, fromIndex, insertIndex) {
  const targetIndex = insertIndex > fromIndex ? insertIndex - 1 : insertIndex;
  return moveItemToIndex(rows, fromIndex, targetIndex);
}

// Direkt über oder unter dem gezogenen Eintrag ändert ein Loslassen nichts.
function isNoOpInsert(fromIndex, insertIndex) {
  return insertIndex === fromIndex || insertIndex === fromIndex + 1;
}

function hasUnsavedSteps(queue) {
  return Boolean(queue?.inFlight || queue?.pending);
}

function modeLabel(mode) {
  return mode === ORDER_MODES.DEPARTMENT ? "Abteilungen" : "Mitarbeitende";
}

function ModeIcon({ mode }) {
  return mode === ORDER_MODES.DEPARTMENT ? <IconDepartment fontSize="small" /> : <PersonOutlineIcon fontSize="small" />;
}

function OrderModeButton({ mode, activeMode, onClick }) {
  const isActive = activeMode === mode;

  return (
    <button
      type="button"
      className={`spv2-order-mode-btn${isActive ? " is-active" : ""}`}
      onClick={onClick}
      aria-pressed={isActive}
    >
      <ModeIcon mode={mode} />
      <span>{modeLabel(mode)}</span>
    </button>
  );
}

function OrderSaveStatus({ status }) {
  if (status === SAVE_STATUS.SAVING) {
    return (
      <span className="spv2-order-save-status" role="status">
        <ShiftplanBusyDots label="Reihenfolge wird gespeichert" />
        <span>Wird gespeichert …</span>
      </span>
    );
  }

  if (status === SAVE_STATUS.SAVED) {
    return (
      <span className="spv2-order-save-status is-saved" role="status">
        <CheckRoundedIcon fontSize="inherit" />
        <span>Gespeichert</span>
      </span>
    );
  }

  return null;
}

function OrderRow({ row, index, rowCount, isDragging, dropPosition, disabled, onMove, onDragStart, onDragEnd }) {
  const handleDragStart = (event) => {
    if (disabled) {
      event.preventDefault();
      return;
    }

    // Die ganze Zeile hängt am Zeiger, nicht nur das Griff-Symbol.
    const rowElement = event.currentTarget.closest(".spv2-order-row");
    if (rowElement && typeof event.dataTransfer?.setDragImage === "function") {
      const rect = rowElement.getBoundingClientRect();
      event.dataTransfer.setDragImage(rowElement, event.clientX - rect.left, event.clientY - rect.top);
    }
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
    onDragStart(index);
  };

  return (
    <Box
      className={`spv2-order-row${isDragging ? " is-dragging" : ""}${disabled ? " is-disabled" : ""}`}
      data-order-index={index}
    >
      {dropPosition ? (
        <span className={`spv2-order-drop-line is-${dropPosition}`} data-testid="order-drop-line" aria-hidden="true" />
      ) : null}
      <Box
        className="spv2-order-row-drag"
        draggable={!disabled}
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
        title="Ziehen zum Sortieren"
        aria-label={`${row.name} ziehen`}
      >
        <IconDrag fontSize="small" />
      </Box>
      <Box className="spv2-order-row-index">{index + 1}</Box>
      <Box className="spv2-order-row-copy">
        <Typography className="spv2-order-row-name">{row.name}</Typography>
        {row.metaLabel ? <Typography className="spv2-order-row-meta">{row.metaLabel}</Typography> : null}
      </Box>
      <Box className="spv2-order-row-actions">
        <ShiftplanTooltip title="Nach oben">
          <span>
            <IconButton
              className="spv2-order-row-action"
              size="small"
              onClick={() => onMove(index, "up")}
              disabled={disabled || index <= 0}
              aria-label={`${row.name} nach oben verschieben`}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </span>
        </ShiftplanTooltip>
        <ShiftplanTooltip title="Nach unten">
          <span>
            <IconButton
              className="spv2-order-row-action"
              size="small"
              onClick={() => onMove(index, "down")}
              disabled={disabled || index >= rowCount - 1}
              aria-label={`${row.name} nach unten verschieben`}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </span>
        </ShiftplanTooltip>
      </Box>
    </Box>
  );
}

/*
  Die Liste nimmt das Ziehen als Ganzes entgegen, nicht jede Zeile für sich:
  so zählt auch der Abstand zwischen zwei Zeilen und der Platz unter der
  letzten, und die Einfügestelle folgt dem Zeiger ohne Sprünge.
*/
function OrderList({ rows, dragState, disabled, onMove, onDragStart, onDragEnd, onDragPosition, onDrop, onDragExit }) {
  const listRef = useRef(null);

  if (!rows.length) {
    return (
      <Box className="spv2-order-empty">
        <Typography className="spv2-order-empty-title">Keine Einträge</Typography>
      </Box>
    );
  }

  const getInsertIndex = (clientY) => {
    const rowElements = listRef.current ? Array.from(listRef.current.querySelectorAll("[data-order-index]")) : [];
    const rowBelowPointer = rowElements.find((element) => {
      const rect = element.getBoundingClientRect();
      return clientY < rect.top + rect.height / 2;
    });
    return rowBelowPointer ? Number(rowBelowPointer.dataset.orderIndex) : rowElements.length;
  };

  const isDraggingRow = dragState.fromIndex !== null;
  const showsDropLine =
    isDraggingRow && dragState.insertIndex !== null && !isNoOpInsert(dragState.fromIndex, dragState.insertIndex);

  const getDropPosition = (index) => {
    if (!showsDropLine) return null;
    if (dragState.insertIndex === index) return "before";
    if (dragState.insertIndex === rows.length && index === rows.length - 1) return "after";
    return null;
  };

  const handleDragOver = (event) => {
    if (!isDraggingRow || disabled) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    onDragPosition(getInsertIndex(event.clientY));
  };

  const handleDrop = (event) => {
    if (!isDraggingRow) return;
    event.preventDefault();
    onDrop(getInsertIndex(event.clientY));
  };

  const handleDragLeave = (event) => {
    if (listRef.current?.contains(event.relatedTarget)) return;
    onDragExit();
  };

  return (
    <Box
      ref={listRef}
      className={`spv2-order-list${isDraggingRow ? " is-sorting" : ""}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
    >
      {rows.map((row, index) => (
        <OrderRow
          key={row.id}
          row={row}
          index={index}
          rowCount={rows.length}
          isDragging={dragState.fromIndex === index}
          dropPosition={getDropPosition(index)}
          disabled={disabled}
          onMove={onMove}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        />
      ))}
    </Box>
  );
}

export function OrderSettingsContent({
  active = true,
  initialMode = ORDER_MODES.EMPLOYEE,
  employees = [],
  departments = [],
  onSaveEmployees,
  onResetEmployees,
  onSaveDepartments,
  onResetDepartments,
  onFeedback,
}) {
  const employeeRows = useMemo(() => normalizeRows(employees, "id"), [employees]);
  const departmentRows = useMemo(() => normalizeRows(departments, "departmentId"), [departments]);
  const [activeMode, setActiveMode] = useState(initialMode);
  const [draftEmployees, setDraftEmployees] = useState(employeeRows);
  const [draftDepartments, setDraftDepartments] = useState(departmentRows);
  const [saveStatus, setSaveStatus] = useState(SAVE_STATUS.IDLE);
  const [isResetting, setIsResetting] = useState(false);
  const [dragState, setDragState] = useState(EMPTY_DRAG);
  // Je Bereich: der Stand, der noch zum Server muss, und ob gerade einer unterwegs ist.
  const savesRef = useRef({
    [ORDER_MODES.EMPLOYEE]: { pending: null, inFlight: false },
    [ORDER_MODES.DEPARTMENT]: { pending: null, inFlight: false },
  });
  const callbacksRef = useRef({});
  callbacksRef.current = { onSaveEmployees, onSaveDepartments, onFeedback };

  useEffect(() => {
    if (!active) return;
    setActiveMode(initialMode === ORDER_MODES.DEPARTMENT ? ORDER_MODES.DEPARTMENT : ORDER_MODES.EMPLOYEE);
    setDragState(EMPTY_DRAG);
    setSaveStatus(SAVE_STATUS.IDLE);
  }, [active, initialMode]);

  /*
    Neuer Stand vom Plan – nach dem Speichern, dem Zurücksetzen oder einem
    Neuladen. Solange eigene Schritte unterwegs sind, bleibt die Liste, wie sie
    ist: sonst überschriebe die Antwort auf den ersten Schritt den zweiten, den
    man schon gemacht hat.
  */
  useEffect(() => {
    if (!active || hasUnsavedSteps(savesRef.current[ORDER_MODES.EMPLOYEE])) return;
    setDraftEmployees(employeeRows);
  }, [active, employeeRows]);

  useEffect(() => {
    if (!active || hasUnsavedSteps(savesRef.current[ORDER_MODES.DEPARTMENT])) return;
    setDraftDepartments(departmentRows);
  }, [active, departmentRows]);

  useEffect(() => {
    if (saveStatus !== SAVE_STATUS.SAVED) return undefined;
    const timeoutId = window.setTimeout(() => setSaveStatus(SAVE_STATUS.IDLE), SAVED_HINT_DURATION);
    return () => window.clearTimeout(timeoutId);
  }, [saveStatus]);

  const flushSaves = useCallback(async (mode) => {
    const queue = savesRef.current[mode];
    if (queue.inFlight || !queue.pending) return;

    const rows = queue.pending;
    queue.pending = null;
    queue.inFlight = true;
    setSaveStatus(SAVE_STATUS.SAVING);

    let failed = false;
    try {
      const { onSaveDepartments: saveDepartments, onSaveEmployees: saveEmployees } = callbacksRef.current;
      await (mode === ORDER_MODES.DEPARTMENT ? saveDepartments : saveEmployees)?.(rows);
    } catch (_error) {
      failed = true;
      // Was noch wartet, baut auf dem abgelehnten Stand auf.
      queue.pending = null;
      callbacksRef.current.onFeedback?.("Die Reihenfolge konnte nicht gespeichert werden.", "error", {
        duration: ERROR_TOAST_DURATION,
      });
    } finally {
      queue.inFlight = false;
    }

    if (queue.pending) {
      flushSaves(mode);
      return;
    }
    setSaveStatus(failed ? SAVE_STATUS.IDLE : SAVE_STATUS.SAVED);
  }, []);

  const activeRows = activeMode === ORDER_MODES.DEPARTMENT ? draftDepartments : draftEmployees;
  const isDepartmentMode = activeMode === ORDER_MODES.DEPARTMENT;

  const applyRows = (nextRows) => {
    if (nextRows === activeRows) return;

    if (activeMode === ORDER_MODES.DEPARTMENT) {
      setDraftDepartments(nextRows);
    } else {
      setDraftEmployees(nextRows);
    }

    savesRef.current[activeMode].pending = nextRows;
    flushSaves(activeMode);
  };

  const handleMove = (index, direction) => {
    if (isResetting) return;
    applyRows(moveItem(activeRows, index, direction));
  };

  const handleDragStart = (index) => {
    setDragState({ fromIndex: index, insertIndex: null });
  };

  const handleDragPosition = (insertIndex) => {
    setDragState((current) => (current.insertIndex === insertIndex ? current : { ...current, insertIndex }));
  };

  const handleDrop = (insertIndex) => {
    const { fromIndex } = dragState;
    setDragState(EMPTY_DRAG);
    if (fromIndex === null || isResetting) return;
    applyRows(moveItemToInsertIndex(activeRows, fromIndex, insertIndex));
  };

  const handleDragExit = () => {
    setDragState((current) => (current.insertIndex === null ? current : { ...current, insertIndex: null }));
  };

  const handleDragEnd = () => {
    setDragState(EMPTY_DRAG);
  };

  const handleReset = async () => {
    if (isResetting) return;

    setIsResetting(true);
    try {
      if (isDepartmentMode) {
        await onResetDepartments?.();
      } else {
        await onResetEmployees?.();
      }
      onFeedback?.(`${modeLabel(activeMode)} stehen wieder in der Standard-Reihenfolge`);
    } catch (_error) {
      onFeedback?.("Die Reihenfolge konnte nicht zurückgesetzt werden.", "error", { duration: ERROR_TOAST_DURATION });
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Box className="spv2-order-panel">
      <Box className="spv2-settings-section-head">
        <Box className="spv2-settings-section-copy">
          <Typography className="spv2-settings-section-title">Reihenfolge</Typography>
        </Box>
        <Box className="spv2-order-head-actions">
          <OrderSaveStatus status={saveStatus} />
          <Button
            size="small"
            className="spv2-order-reset-btn spv2-order-reset-btn--light"
            startIcon={
              isResetting ? (
                <ShiftplanBusyDots label="Reihenfolge wird zurückgesetzt" />
              ) : (
                <RestartAltOutlinedIcon fontSize="small" />
              )
            }
            onClick={handleReset}
            disabled={isResetting || saveStatus === SAVE_STATUS.SAVING}
          >
            Zurücksetzen
          </Button>
        </Box>
      </Box>

      <Box className="spv2-order-mode-switch" aria-label="Reihenfolge-Bereich">
        <OrderModeButton
          mode={ORDER_MODES.EMPLOYEE}
          activeMode={activeMode}
          onClick={() => setActiveMode(ORDER_MODES.EMPLOYEE)}
        />
        <OrderModeButton
          mode={ORDER_MODES.DEPARTMENT}
          activeMode={activeMode}
          onClick={() => setActiveMode(ORDER_MODES.DEPARTMENT)}
        />
      </Box>

      <OrderList
        rows={activeRows}
        dragState={dragState}
        disabled={isResetting}
        onMove={handleMove}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragPosition={handleDragPosition}
        onDrop={handleDrop}
        onDragExit={handleDragExit}
      />
    </Box>
  );
}
